"use client"

import RichTextWrapper from "@/ui/common/bit/RichTextWrapper"
import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { Fragment } from "react"
import ButtonWithArrow from "ui/common/bit/ButtonWithArrow"
import LottiePlayer from "ui/common/bit/LottiePlayer"
import { isLottieFile } from "ui/common/bit/isLottieFile"
import { colors, mediaQueries, sizes } from "ui/theme/theme"
import { getNonUmbracoUrl } from "utils"
import { StyledHeroBanner } from "../HeroBanner"
interface MultiUrlPickerItem {
  name: string
  url: string
  target?: string | null
}

interface DetailsInfoBlock {
  properties: Array<{
    propertyName: string
    propertyValue: any
  }>
}

interface NotFoundBlockProps {
  data: any
  id?: string
}

const StyledHero = styled(StyledHeroBanner)<{ component?: string }>`
  display: flex;
  padding: ${sizes.baseQuadric} 0;
`

const StyledContainer = styled.div`
  display: grid;
  gap: ${sizes.baseTriple};

  ${mediaQueries.mobile} {
    padding-top: 165px;
    padding-bottom: 20px;
    padding-inline-start: 16px;
    padding-inline-end: 16px;
    & > :first-child {
      order: 2;
    }
    & > :nth-child(2) {
      order: 1;
    }
  }

  ${mediaQueries.tablet} {
    grid-template-columns: repeat(2, 1fr);
    align-items: center;
    padding-inline-start: 80px;
    padding-inline-end: 80px;
  }
  ${mediaQueries.desktop} {
    padding-inline-start: unset;
    padding-inline-end: unset;
  }
`

const ImageWrapper = styled.div`
  width: 100%;
  max-width: 580px;
  img {
    width: 100%;
    height: auto;
    object-fit: contain;
  }
`

const StyledQualificationContainer = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: ${sizes.baseQuadric};
  row-gap: ${sizes.baseSingle};
  margin-top: ${sizes.baseQuadric};
  width: 350px;
  ${mediaQueries.mobile} {
    grid-template-columns: 1fr 1fr;
    width: 100%;
  }
  & > *:nth-child(even) {
    text-align: end;
  }
`

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.baseQuadric};
  font-size: 24px;
  color: ${colors.black};
  line-height: 1.5;
  p {
    margin: 0;
  }
  ${mediaQueries.mobile} {
    text-align: center;
    font-size: 16px;
    align-items: center;
  }
`
const StyledTitle = styled(Typography)<{ component?: string }>`
  text-align: start;
  color: ${colors.black};

  ${mediaQueries.mobile} {
    text-align: center;
    font-size: 31px;
  }
`
const StyledLinksBlock = styled.div`
  ${mediaQueries.mobile} {
    width: 100%;
    text-align: center;
    align-items: center;
    display: flex;
    flex-direction: column;
  }
`

const StyledInfoTitleTypography = styled(Typography)`
  color: ${colors.red};
`
const StyledPhoneTypography = styled(Typography)`
  /* Inherit default body3 styles, no
                    extra needed unless design requires */
`
const StyledPhoneLink = styled.a`
  color: inherit;
  text-decoration: none;
`

const NotFoundBlock = ({ data, id }: NotFoundBlockProps) => {
  const t = useTranslations("PageNotFound")
  const title = data?.title?.value || t("title")
  const description = data?.description?.richText || t("message")
  const buttonLink: MultiUrlPickerItem | undefined = data?.button?.links?.[0]
  const subTitle = data?.subTitle?.value || t("alternativeHeader")
  const detailsInfo: DetailsInfoBlock[] = data?.detailsInfo?.blocks || []
  const imageUrl = data?.image?.mediaItems?.[0]?.url || "/pageNotFound.png"
  const imageAlt = data?.image?.mediaItems?.[0]?.altText || title
  const backgroundColor = data?.backgroundColor?.value || colors.beige
  //bgcolor={backgroundColor}
  return (
    <StyledHero
      id={id}
      isSwiper={false}
      component="section"
      bgcolor={backgroundColor}
    >
      <StyledContainer>
        <StyledContent>
          <StyledTitle variant="h2" component="h1">
            {title}
          </StyledTitle>
          {description && (
            <RichTextWrapper
              richText={description}
              paragraphFontWeight={400}
              mobileParagraphFontSize="16px"
            />
          )}
          {buttonLink && (
            <ButtonWithArrow
              href={getNonUmbracoUrl(buttonLink.url)}
              target={buttonLink.target || undefined}
              sx={{ minWidth: 200, fontWeight: 700, fontSize: "20px" }}
            >
              {buttonLink.name}
            </ButtonWithArrow>
          )}
          <StyledLinksBlock>
            <Typography variant="h5" textAlign="start" component="h2">
              {subTitle}
            </Typography>
            <StyledQualificationContainer>
              {detailsInfo.map((block, idx) => {
                const infoTitle = block.properties.find(
                  (p) => p.propertyName === "infoTitle",
                )?.propertyValue?.value
                const phoneObj = block.properties.find(
                  (p) => p.propertyName === "phone",
                )?.propertyValue
                const phoneLink: MultiUrlPickerItem | undefined =
                  phoneObj?.links?.[0]
                return (
                  <Fragment key={`fragment-${idx}`}>
                    <StyledInfoTitleTypography
                      key={`label-${idx}`}
                      variant="body3"
                    >
                      {infoTitle}
                    </StyledInfoTitleTypography>
                    {phoneLink && (
                      <StyledPhoneTypography
                        key={`phone-${idx}`}
                        variant="body3"
                      >
                        <StyledPhoneLink
                          href={phoneLink.url}
                          target={phoneLink.target || undefined}
                          rel="noopener noreferrer"
                        >
                          {phoneLink.name}
                        </StyledPhoneLink>
                      </StyledPhoneTypography>
                    )}
                  </Fragment>
                )
              })}
            </StyledQualificationContainer>
          </StyledLinksBlock>
        </StyledContent>
        <ImageWrapper>
          {imageUrl && isLottieFile(imageUrl) ? (
            <LottiePlayer
              lottieUrl={imageUrl}
              ariaLabel={imageAlt}
              width={581}
              height={300}
              style={{ maxWidth: "100%", height: 300 }}
            />
          ) : (
            <Image
              src={imageUrl}
              alt={imageAlt}
              width={581}
              height={300}
              priority
            />
          )}
        </ImageWrapper>
      </StyledContainer>
    </StyledHero>
  )
}

export default NotFoundBlock
